import { defaultBusiness as NAP } from "@/lib/site-config";
// Lightweight conversion tracking — fires to GA4 if present AND persists to Supabase
// via a fire-and-forget server function so the admin dashboard can show real numbers.

import { recordEvent } from "@/lib/analytics.functions";
import { getCachedRouting } from "@/hooks/usePublicConfig";
import { resolveRouting } from "@/lib/site-content.functions";

type TrackingEvent =
  | "call_click"
  | "whatsapp_click"
  | "booking_request_start"
  | "booking_request_submit"
  | "contact_submit"
  | "directions_click"
  | "directions_mode_change"
  | "directions_origin_set"
  | "directions_start_route"
  | "directions_whatsapp_click"
  | "maps_open_click"
  | "email_click"
  | "service_cta_click"
  | "gallery_open"
  | "review_link_click"
  | "google_profile_click"
  | "instagram_click"
  | "linktree_click"
  | "qr_scan_view"
  | "area_page_view"
  | "mot_form_start"
  | "mot_form_submit"
  | "mot_slot_selected"
  | "mot_slot_confirm_request"
  | "mot_callback_open"
  | "mot_callback_request"
  | "mot_callback_sent"
  | "mot_no_slot_matched"
  | "guide_card_click"
  | "guide_card_call"
  | "driver_support_cta_click"
  | "driver_support_strip_click"
  | "area_driver_link_click"
  | "homepage_local_pack_cta_click"
  | "service_money_page_cta_click"
  | "area_conversion_cta_click"
  | "review_request_click"
  | "referral_share_click"
  | "google_maps_verify_cta_click"
  | "local_rank_snapshot_logged"
  | "resource_copy_link"
  | "resource_whatsapp_share"
  | "resource_print"
  | "resource_backlink_invite_view"
  | "resource_external_share_click"
  | "resource_hub_cta_click"
  | "cta_mode_view"
  | "cta_phone_click"
  | "cta_form_open"
  | "cta_whatsapp_click"
  | "schema_preview_open"
  | "schema_preview_rich_results"
  | "lighthouse_run_started"
  | "managed_by_outbound_click"
  | "web_design_contact_click"
  | "web_design_outbound_click"
  | "managed_by_internal_cta_click"
  | "lazy_widget_timeout"
  | "lazy_widget_error"
  | "lazy_widget_retry"
  | "lazy_widget_retry_result"
  | "page_view";

// Prevent duplicate fires within a short window
const recentEvents = new Map<string, number>();

function getDevice(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = sessionStorage.getItem("woodseer_session");
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("woodseer_session", id);
    }
    return id;
  } catch {
    return "";
  }
}

export function trackEvent(event: TrackingEvent, properties?: Record<string, string | number | boolean>) {
  try {
    const key = `${event}_${JSON.stringify(properties || {})}`;
    const now = Date.now();
    const last = recentEvents.get(key);
    if (last && now - last < 1000) return; // debounce 1s
    recentEvents.set(key, now);

    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", event, properties);
    }

    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      console.log(`[track] ${event}`, properties);
    }

    // Fire-and-forget persist to Supabase. Never await — analytics must not block UX.
    if (typeof window !== "undefined") {
      const { location: loc, service, ...restMeta } = (properties || {}) as Record<string, string | number | boolean>;
      recordEvent({
        data: {
          event_name: event,
          page_path: window.location.pathname,
          device: getDevice(),
          location: typeof loc === "string" ? loc : undefined,
          service: typeof service === "string" ? service : undefined,
          metadata: Object.keys(restMeta).length ? restMeta : undefined,
          user_agent: window.navigator.userAgent.slice(0, 500),
          referrer: document.referrer.slice(0, 500) || undefined,
          session_id: getSessionId(),
        },
      }).catch(() => {});
    }
  } catch {
    // Silent fail — tracking should never break UX
  }
}

export interface WhatsAppContext {
  reg?: string;
  name?: string;
  contactTime?: string;
  /** Area or neighbourhood (e.g. "Poplar"). */
  area?: string;
  /** Vehicle make/model (e.g. "VW Golf 2018"). */
  vehicle?: string;
  /** Customer phone, if known. */
  phone?: string;
  /** Free-text notes / issue description. */
  notes?: string;
  /** "Today", "This week", etc. */
  urgency?: string;
  /** Originating page type — used to tag the WhatsApp lead source.
   *  e.g. "cost" for /cost/* commercial guides, "area" for area pages. */
  pageType?: "cost" | "area" | "service" | "home" | "blog";
}

const MAX_WA_LEN = 900;

function sanitiseField(value: string | undefined): string {
  if (!value) return "";
  return String(value).replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 160);
}

/**
 * Build a WhatsApp URL whose message includes rich booking context — service,
 * area, vehicle, preferred time, notes — formatted as labelled lines. Empty
 * fields are omitted cleanly. Always falls back to the cached routing-resolved
 * message when no extra context is supplied.
 */
export function getWhatsAppUrl(
  service?: string,
  context?: WhatsAppContext,
): string {
  const baseNumber = "442075372447";
  const serviceLabel = service ? service.replace(/-/g, " ") : "garage service / repair";
  const area = sanitiseField(context?.area) || "East London";
  const vehicle = sanitiseField(context?.vehicle);
  const reg = sanitiseField(context?.reg).toUpperCase();
  const vehicleLine = [reg, vehicle].filter(Boolean).join(" ");
  const preferred = sanitiseField(context?.contactTime || context?.urgency);
  const notes = sanitiseField(context?.notes);
  const name = sanitiseField(context?.name);
  const customerPhone = sanitiseField(context?.phone);

  // Build a labelled message when we have useful extra context.
  const hasExtra = Boolean(area || vehicleLine || preferred || notes);
  if (hasExtra) {
    const lines = [
      `Hi Woodseer Autos, I'd like help with ${serviceLabel}.`,
      `Area: ${area}`,
      vehicleLine ? `Vehicle: ${vehicleLine}` : "",
      preferred ? `Preferred time: ${preferred}` : "",
      notes ? `Issue/notes: ${notes}` : "",
      name || customerPhone ? `From: ${[name, customerPhone].filter(Boolean).join(" · ")}` : "",
      "",
      `Please let me know the earliest availability. If WhatsApp is not convenient, I can call ${NAP.phone}.`,
    ].filter(Boolean).join("\n");
    const msg = lines.length > MAX_WA_LEN ? lines.slice(0, MAX_WA_LEN) : lines;
    if (typeof window === "undefined") {
      return `https://wa.me/${baseNumber}?text=${encodeURIComponent(msg)}`;
    }
    const routing = getCachedRouting();
    const { whatsappNumber } = resolveRouting(routing, service, { reg, name });
    return `https://wa.me/${whatsappNumber || baseNumber}?text=${encodeURIComponent(msg)}`;
  }

  // Fallback: original routing-resolved message (kept for backwards compatibility).
  const suffix = preferred ? ` — preferred contact: ${preferred}` : "";
  if (typeof window === "undefined") {
    const fallbackMsg = service
      ? `Hi, I'd like to book ${serviceLabel}${suffix}`
      : `Hi, I'd like to make an enquiry${suffix}`;
    return `https://wa.me/${baseNumber}?text=${encodeURIComponent(fallbackMsg)}`;
  }
  const routing = getCachedRouting();
  const { whatsappUrl } = resolveRouting(routing, service, { reg, name });
  if (!suffix) return whatsappUrl;
  try {
    const u = new URL(whatsappUrl);
    const text = u.searchParams.get("text") || "";
    u.searchParams.set("text", `${text}${suffix}`);
    return u.toString();
  } catch {
    return whatsappUrl;
  }
}

/** Resolve the phone number (tel: href) for a given service / lead type using cached routing. */
export function getPhoneHref(service?: string): string {
  if (typeof window === "undefined") return NAP.phoneHref;
  const routing = getCachedRouting();
  const { phone } = resolveRouting(routing, service);
  return `tel:${phone.replace(/\s+/g, "")}`;
}

export function isOpenNow(): { open: boolean; message: string } {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const hour = now.getHours();
  const minute = now.getMinutes();
  const time = hour * 60 + minute;

  if (day === 0) {
    return { open: false, message: "Closed · Opens Monday 9am" };
  }

  // Mon-Sat 9:00-18:00
  if (time >= 540 && time < 1080) {
    const closesIn = 1080 - time;
    if (closesIn <= 60) {
      return { open: true, message: `Open · Closes in ${closesIn} min` };
    }
    return { open: true, message: "Open · Closes 6pm" };
  }

  if (day === 6) {
    return { open: false, message: "Closed · Opens Monday 9am" };
  }

  return { open: false, message: "Closed · Opens tomorrow 9am" };
}
